import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { changeAttendees } from "@store/reducers/paper/actions";
import { fetchAttendeesFromAPI } from '../services/attendeeService';
import { seatingForDesign } from '@components/Gala/seatAssignment';
import { mergeFabricDataWithAPIData } from '../services/fabricDataService';
import { SnackbarState } from '../types';
import axios from 'axios';

interface UseSyncAttendeesProps {
  eventId: string;
  id: string;
  tableNameFromUrl: string | null;
  mapRef: React.MutableRefObject<any>;
  floorMapDesignMeta: any;
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  clearMapLayers: () => void;
  processFabricData: (map: any, data: any) => void;
}

export const useSyncAttendees = ({
  eventId,
  id,
  tableNameFromUrl,
  mapRef,
  floorMapDesignMeta,
  showSnackbar,
  clearMapLayers,
  processFabricData
}: UseSyncAttendeesProps) => {
  const dispatch = useDispatch();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const abortControllerRef = useRef<any | null>(null);

  /**
   * Fetches attendee data from the API and synchronizes it with the map layout.
   * This is the core function for refreshing attendee assignments.
   */
  const fetchAndSyncAttendees = async () => {
    // Keep reference to current cancel token for cleanup comparison
    let currentCancelSource: any;
    
    try {
      setIsSyncing(true);
      setSyncError(null);
      
      // Cancel any existing request to prevent race conditions
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('New request initiated');
      }
      
      // Create a new cancel token source for this request
      abortControllerRef.current = axios.CancelToken.source();
      currentCancelSource = abortControllerRef.current; // Keep reference to current source

      const response = await fetchAttendeesFromAPI(eventId, currentCancelSource.token);

      if (response && Array.isArray(response)) {
        // Store raw attendees data in Redux
        dispatch(changeAttendees(response));
        
        // Flatten the array to handle potentially nested attendee data structures
        const flattenedData = response.flat(3); 
        console.log('API Response - Flattened data:', flattenedData);
        
        // Update the map with the synchronized data
        if (mapRef.current && floorMapDesignMeta?.filteredVisualDesigns?.length) {
          const template = floorMapDesignMeta.filteredVisualDesigns.find(item => item.id == id);
          if (template && template.template.pageData[0].templateData) {
            const fabricJsJsonData = template.template.pageData[0].templateData;

            // Who sits where comes from the Seat Assignment module, which owns
            // the seating model and resolves each SeatNo against this room's
            // Tables (ADR-0001/ADR-0002). The Design supplies the room only.
            const attendeesByTable = await seatingForDesign(
              [{ templateData: fabricJsJsonData }],
              async () => flattenedData
            );
            console.log('Seat Assignments by table:', attendeesByTable);

            // Draw those assignments onto the Fabric.js layout
            let mergedData = mergeFabricDataWithAPIData(fabricJsJsonData, attendeesByTable);
            
            if(tableNameFromUrl) {
              // Helper function to normalize table names for comparison
              const normalizeTableName = (name: string) => {
                return name.toLowerCase()
                  .replace(/^table\s*/i, '') // Remove 'table' prefix
                  .replace(/\s+/g, '')      // Remove all spaces
                  .trim();
              };

              // Try to find table with more flexible matching
              let singleTable = mergedData.objects.find((obj: any) => {
                if (!obj.tableData) return false;
                
                const tableNumber = obj.tableData.tableNumber;
                const tableName = obj.tableData.tableName;
                
                const normalizedSearch = normalizeTableName(tableNameFromUrl);
                const normalizedNumber = tableNumber ? normalizeTableName(tableNumber) : '';
                const normalizedName = tableName ? normalizeTableName(tableName) : '';
                
                return normalizedNumber === normalizedSearch || normalizedName === normalizedSearch;
              });
              
              // Check if table exists before modifying mergedData
              if (singleTable) {
                mergedData = {
                  objects: [singleTable]
                };
                console.log(`🎯 Found and filtered to table "${tableNameFromUrl}"`);
              } else {
                // If table not found, show no tables and display warning
                console.warn(`⚠️ Table "${tableNameFromUrl}" not found in layout`);
                showSnackbar(`Table "${tableNameFromUrl}" not found in layout`, 'warning');
                
                // Set mergedData to empty objects array to show no tables
                mergedData = {
                  objects: []
                };
              }
            }
            
            // Clear existing map layers (shapes, markers, text) before re-rendering
            clearMapLayers();
            
            // Re-render the map with the updated and merged data
            processFabricData(mapRef.current, mergedData);
            
            console.log('Successfully synchronized data with API');
            setLastSyncTime(new Date());
          } else {
            console.warn('Floor map template or its data not found.');
            setSyncError('Floor map template data missing.');
            showSnackbar('Sync failed: Template data missing', 'error');
          }
        } else {
          // More specific error messaging
          if (!mapRef.current) {
            console.warn('Map not initialized yet.');
            setSyncError('Map is still initializing.');
            showSnackbar('Map still loading, please wait...', 'warning');
          } else if (!floorMapDesignMeta?.filteredVisualDesigns?.length) {
            console.warn('Visual designs not loaded yet.');
            setSyncError('Design data is still loading.');
            showSnackbar('Design data loading, please wait...', 'warning');
          } else {
            console.warn('Map not initialized or no visual designs found.');
            setSyncError('Map or design data not available.');
            showSnackbar('Sync failed: Map not ready', 'error');
          }
        }
      } else {
        console.warn('No data received from API or invalid format');
        setSyncError('No data received from API or invalid format.');
        showSnackbar('Sync failed: No data received', 'error');
      }
    } catch (error: any) {
      // Handle cancellation errors gracefully
      if (axios.isCancel(error)) {
        console.log('Fetch cancelled:', error.message);
        // No need to set syncError for cancels, as it's an intentional cancellation
        return; // Return early for cancelled requests
      } else {
        console.error('Error fetching attendees:', error);
        setSyncError(error.message || 'Unknown error occurred during sync.');
        showSnackbar('Sync failed: ' + (error.message || 'Unknown error'), 'error');
      }
    } finally {
      setIsSyncing(false);
      // Only clear the cancel source if it's the same one we created for this request
      // This prevents clearing a newer source that might have been created by a subsequent request
      if (abortControllerRef.current === currentCancelSource) {
        abortControllerRef.current = null;
      }
    }
  };

  return {
    isSyncing,
    lastSyncTime,
    syncError,
    fetchAndSyncAttendees,
    abortControllerRef
  };
}; 